import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { CheckCircle, XCircle, Clock, Gear, Warning, ShieldCheck, Spinner } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { approvalsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const statusConfig = {
  pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { color: 'bg-rose-100 text-rose-700', icon: XCircle },
};

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [allApprovals, setAllApprovals] = useState([]);
  const [config, setConfig] = useState({ rules: [], approval_enabled: false });
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const isAdmin = ['hospital_admin', 'super_admin'].includes(user?.role);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'pending') { const r = await approvalsApi.pending(); setPending(r.data || []); }
      else if (tab === 'my') { const r = await approvalsApi.myRequests(); setMyReqs(r.data || []); }
      else if (tab === 'all') { const r = await approvalsApi.all({}); setAllApprovals(r.data || []); }
      else if (tab === 'config') { const r = await approvalsApi.getConfig(); setConfig(r.data || { rules: [], approval_enabled: false }); }
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  const handleAction = async (approve) => {
    if (!actionDialog) return;
    if (!approve && !remarks.trim()) { toast.error('Rejection remarks required'); return; }
    setProcessing(true);
    try {
      if (approve) await approvalsApi.approve(actionDialog.id, { remarks });
      else await approvalsApi.reject(actionDialog.id, { remarks });
      toast.success(`Request ${approve ? 'approved' : 'rejected'}`);
      setActionDialog(null); setRemarks(''); loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setProcessing(false);
  };

  const saveConfig = async () => {
    try {
      await approvalsApi.updateConfig(config);
      toast.success('Configuration saved');
    } catch (e) { toast.error('Failed to save'); }
  };

  const toggleRule = (idx) => {
    const rules = [...config.rules];
    rules[idx] = { ...rules[idx], enabled: !rules[idx].enabled };
    setConfig({ ...config, rules });
  };

  const updateThreshold = (idx, val) => {
    const rules = [...config.rules];
    rules[idx] = { ...rules[idx], threshold_amount: parseInt(val) || 0 };
    setConfig({ ...config, rules });
  };

  return (
    <div className="space-y-5" data-testid="approvals-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-slate-800 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-500" weight="duotone" /> Approvals
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Maker-Checker workflow management</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="my" data-testid="tab-my-requests">My Requests</TabsTrigger>
          {isAdmin && <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>}
          {isAdmin && <TabsTrigger value="config" data-testid="tab-config"><Gear className="h-3.5 w-3.5 mr-1" /> Config</TabsTrigger>}
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {loading ? <div className="text-center py-10"><Spinner className="h-6 w-6 text-indigo-500 animate-spin mx-auto" /></div> :
            pending.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No pending approvals</div> :
            <div className="space-y-3">{pending.map(a => <ApprovalCard key={a.id} item={a} onAction={setActionDialog} showAction />)}</div>}
        </TabsContent>

        <TabsContent value="my" className="mt-4">
          {loading ? <div className="text-center py-10"><Spinner className="h-6 w-6 text-indigo-500 animate-spin mx-auto" /></div> :
            myReqs.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No approval requests submitted</div> :
            <div className="space-y-3">{myReqs.map(a => <ApprovalCard key={a.id} item={a} />)}</div>}
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          {loading ? <div className="text-center py-10"><Spinner className="h-6 w-6 text-indigo-500 animate-spin mx-auto" /></div> :
            <div className="space-y-3">{allApprovals.map(a => <ApprovalCard key={a.id} item={a} />)}</div>}
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <Card className="bg-white border border-slate-200">
            <CardContent className="pt-5 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Enable Maker-Checker Workflow</h3>
                  <p className="text-xs text-slate-500">When enabled, configured actions require approval before execution</p>
                </div>
                <Switch checked={config.approval_enabled} onCheckedChange={v => setConfig({...config, approval_enabled: v})} data-testid="approval-toggle" />
              </div>
              <div className="space-y-3">
                {config.rules?.map((rule, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(i)} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{rule.label}</p>
                      <p className="text-[10px] text-slate-400">Maker: {rule.maker_roles?.join(', ')} | Checker: {rule.checker_roles?.join(', ')}</p>
                    </div>
                    <div className="w-40">
                      <Label className="text-[10px]">Threshold (INR)</Label>
                      <Input type="number" value={rule.threshold_amount || 0} onChange={e => updateThreshold(i, e.target.value)} className="h-8 text-xs" />
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveConfig} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="save-config-btn">Save Configuration</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Review Approval Request</DialogTitle>
            <DialogDescription>{actionDialog?.action?.replace(/_/g, ' ')} by {actionDialog?.maker_name}</DialogDescription>
          </DialogHeader>
          {actionDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                <p><strong>Action:</strong> {actionDialog.action?.replace(/_/g, ' ')}</p>
                <p><strong>By:</strong> {actionDialog.maker_name}</p>
                {actionDialog.amount > 0 && <p><strong>Amount:</strong> INR {actionDialog.amount?.toLocaleString()}</p>}
                {actionDialog.description && <p><strong>Details:</strong> {actionDialog.description}</p>}
              </div>
              <Textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks (required for rejection)" rows={3} className="text-sm" data-testid="approval-remarks" />
              <div className="flex gap-2">
                <Button onClick={() => handleAction(true)} disabled={processing} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1" data-testid="approve-btn">
                  <CheckCircle className="h-4 w-4" /> Approve
                </Button>
                <Button onClick={() => handleAction(false)} disabled={processing} className="flex-1 bg-rose-500 hover:bg-rose-600 text-white gap-1" data-testid="reject-btn">
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApprovalCard({ item, onAction, showAction }) {
  const cfg = statusConfig[item.status] || statusConfig.pending;
  const Icon = cfg.icon;
  return (
    <Card className="bg-white border border-slate-200 hover:shadow-sm transition-shadow">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`text-[9px] border-0 ${cfg.color}`}><Icon className="h-3 w-3 mr-0.5" weight="bold" /> {item.status?.toUpperCase()}</Badge>
              <Badge className="text-[9px] bg-slate-100 text-slate-600 border-0">{item.action?.replace(/_/g, ' ')}</Badge>
            </div>
            <p className="text-sm font-medium text-slate-800">{item.description || item.action?.replace(/_/g, ' ')}</p>
            <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
              <span>By: {item.maker_name}</span>
              {item.amount > 0 && <span>INR {item.amount?.toLocaleString()}</span>}
              <span>{new Date(item.created_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>
            </div>
            {item.checker_name && <p className="text-[10px] text-slate-500 mt-1">{item.status === 'approved' ? 'Approved' : 'Rejected'} by {item.checker_name}{item.checker_remarks ? `: ${item.checker_remarks}` : ''}</p>}
          </div>
          {showAction && item.status === 'pending' && onAction && (
            <Button size="sm" onClick={() => onAction(item)} className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs shrink-0">Review</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
